import { DEBUG, DEBUG_ALERT_MSG } from '../config';
import { isCurrentTabMaster } from './establishMultitabRole';
import { throttle } from './schedulers';

const CHUNK_RELOAD_KEY = 'tt_chunk_reload_ts';
const CHUNK_RELOAD_COOLDOWN_MS = 15000;

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  let message = '';
  if (typeof err === 'string') {
    message = err;
  } else if (err instanceof Error) {
    message = err.message;
  } else if (typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
    message = err.message;
  }
  const lower = message.toLowerCase();
  return (
    lower.includes('dynamically imported module')
    || lower.includes('failed to fetch dynamically imported module')
    || lower.includes('importing a module script failed')
    || lower.includes('error loading dynamically imported module')
    || lower.includes('loading chunk')
  );
}

function handleChunkLoadRecovery(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    const now = Date.now();
    if (now - lastReload > CHUNK_RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage might fail in restricted environments
  }
  return false;
}

// Vite emits 'vite:preloadError' when a dynamic import chunk fails to fetch
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  handleChunkLoadRecovery();
});

let showError = true;
let error: Error | undefined;

window.addEventListener('error', handleErrorEvent);
window.addEventListener('unhandledrejection', handleErrorEvent);

if (DEBUG) {
  window.addEventListener('focus', () => {
    if (!isCurrentTabMaster()) {
      return;
    }
    showError = true;
    if (error) {
      window.alert(getErrorMessage(error));
      error = undefined;
    }
  });
  window.addEventListener('blur', () => {
    if (!isCurrentTabMaster()) {
      return;
    }
    showError = false;
  });
}

const throttleError = throttle((err) => {
  if (showError) {
    window.alert(getErrorMessage(err));
  } else {
    error = err;
  }
}, 1500);

export function handleError(err: Error) {
  // eslint-disable-next-line no-console
  console.error(err);

  if (isChunkLoadError(err)) {
    if (handleChunkLoadRecovery()) {
      return;
    }
  }

  if (DEBUG) {
    throttleError(err);
  }
}

function handleErrorEvent(e: ErrorEvent | PromiseRejectionEvent) {
  const err = e instanceof ErrorEvent ? (e.error || e.message) : e.reason;

  if (isChunkLoadError(err)) {
    e.preventDefault();
    if (handleChunkLoadRecovery()) {
      return;
    }
  }

  if (e instanceof ErrorEvent) {
    // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
    if (e.message === 'ResizeObserver loop limit exceeded') {
      return;
    }

    // Flood wait errors
    if (e.message.includes('A wait of')) {
      return;
    }
  }

  e.preventDefault();
  handleError(err);
}

function getErrorMessage(err: Error) {
  return `${DEBUG_ALERT_MSG}\n\n${(err?.message) || err}\n${err?.stack}`;
}
